import { answeringLineLabel } from "@repo/shared";
import type { MemberRepresentative } from "@repo/shared/types";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * QL-505 — one member's row inside an expanded mode row or a declaration
 * group. Purely presentational: the caller supplies the name and the
 * already-built action/content node (reusing the same per-mode renderers the
 * single-subject rows use), so a member's line is always the same UI a
 * single answering subject gets, just indented under the group.
 *
 * QL-660 — a member whose own client has a POA/guardian says who answers for
 * them, tense-aware: "answers through {PoA}" until the slot has an answer,
 * "answered by {PoA}" after. QL-653 — a member with 2+ representatives is one
 * line per representative (`subjectId` is the representative's own id), named
 * by them: "{PoA} ({role}), on behalf of {member}". The wording lives in
 * `answeringLineLabel`.
 *
 * QL-786 — `controlId` names the child control the line's text labels. Set
 * it only when the child is a form control (the declaration checkbox): the
 * name then renders as its `<label>`, so clicking it toggles the control.
 * Lines whose child is an action button (quiz / questionnaire) leave it unset.
 * `controlDisabled` mirrors the control's disabled state so the name does not
 * show a pointer it cannot honour; the label stays, as the control's name.
 */
export function MemberLine({
  subjectId,
  name,
  memberRepresentative,
  answered = false,
  language = "en_GB",
  controlId,
  controlDisabled = false,
  children,
}: {
  /** The line's key (a representative's own `client_party.id` on a member's expanded line). */
  subjectId: string;
  name: string;
  memberRepresentative?: MemberRepresentative | null;
  /** Whether the member's slot has an answer. */
  answered?: boolean;
  language?: string;
  /** The id of the child form control this line's text labels, if any. */
  controlId?: string;
  /** Whether that control is disabled — the name then gets a not-allowed cursor. */
  controlDisabled?: boolean;
  children: ReactNode;
}) {
  const label = answeringLineLabel(
    { subjectId, memberId: subjectId, name, memberRepresentative: memberRepresentative ?? null },
    answered,
    language,
  );
  return (
    <div className="flex flex-col gap-1 border-l-2 border-muted pl-3 py-1 sm:min-h-10 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      {controlId ? (
        <label
          htmlFor={controlId}
          className={cn(
            "min-w-0 truncate text-sm text-muted-foreground",
            controlDisabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
          title={label}
        >
          {label}
        </label>
      ) : (
        <span className="min-w-0 truncate text-sm text-muted-foreground" title={label}>
          {label}
        </span>
      )}
      <div className="flex min-h-8 shrink-0 items-center sm:justify-end">{children}</div>
    </div>
  );
}
